import { InvoiceParams } from 'models/Invoice'
import { RecurrencesParams } from 'models/Recurrences'
import moment from 'moment'
import { ChargeRecurrenceBodyModalForm } from 'templates/Charge/interface'
import {
  calculateTotalValue,
  getDefaultDaysToReport,
  getLimitDays
} from 'templates/Charge/sharedCharge.utils'
import { v4 as uuidv4 } from 'uuid'

import { InternalIdentifier } from 'components/IdentifyInternal/IdentifyInternal.interface'
import { enumNature } from 'components/Modal/ModalInvoice/ModalInvoice.utils'

import { formatCents, onlyNumber } from 'utils'
import { FREQUENCY_NAME_TO_ENUM, presetToPair } from 'utils/frequency'
import { convertTagsStringToArray } from 'utils/tags'

import { DiscountField } from './Components/Discounts/Discounts.interface'

/**
 * Maps a charge object to form values for editing
 * When status is 'expiring' or 'no_payment', adds a "Multa/Juros" item with the difference
 */
export const mapChargeToFormValues = (
  charge: InvoiceParams
): Partial<ChargeRecurrenceBodyModalForm> => {
  // Handle discounts - agora retorna como parte dos valores do formulário
  const discounts: DiscountField[] = []
  if (
    charge?.discounts &&
    Array.isArray(charge.discounts) &&
    charge.discounts.length > 0
  ) {
    charge.discounts.forEach(({ amount, days, policy }) => {
      if (amount !== undefined && days !== undefined && policy !== undefined) {
        const dueDate = new Date(charge?.dueDate)
        dueDate.setDate(dueDate.getDate() + 1)
        dueDate.setDate(dueDate.getDate() - Number(days))

        let amountToSave
        if (policy == 'percentage') {
          amountToSave = amount
        } else {
          amountToSave = formatCents(amount) || 0
        }

        discounts.push({
          amount: amountToSave.toString(),
          date: dueDate,
          uuid: uuidv4(),
          type: policy == 'percentage' ? '0' : '1'
        })
      }
    })
  }

  // Handle metadata - será processado no retorno da função
  const internalIdentifiers: InternalIdentifier[] = []
  if (charge?.metadata && typeof charge.metadata === 'object') {
    Object.keys(charge.metadata).forEach((key) => {
      if (key && charge.metadata[key] !== undefined) {
        internalIdentifiers.push({
          name: key,
          value: charge.metadata[key]
        })
      }
    })
  }

  const parseAmount = onlyNumber(formatCents(charge.amountBilled)) || '0'

  // For expired billings, set due date to today
  const isExpiredBilling =
    charge.status === 'expiring' || charge.status === 'no_payment'
  const dueDate = isExpiredBilling
    ? moment().toDate()
    : moment(charge.dueDate).toDate()

  // Find credit card installments payment if exists
  const parceledCreditCard = charge?.payments.find(
    (payment) => payment.paymentType === 'credit_card_installments'
  )
  const isCustomPolicy =
    charge?.nfsePolicy === 'on_before_due_date' ||
    charge?.nfsePolicy === 'on_after_due_date'
  // Map charge data to form values
  const daysDiff = moment(charge.expirationField).diff(
    moment(charge.dueDate),
    'days'
  )

  return {
    expirationField: (daysDiff > 0 ? daysDiff : 60).toString(), // Default para 60 se o cálculo der negativo
    amountBilled: parseAmount,
    hasItemsOnDescription: (charge.description || '').includes(
      '{{itens_da_cobranca}}'
    ),
    hasIdentifyInternal: internalIdentifiers.length > 0,
    internalIdentifiers,
    discounts, // Adicionar descontos ao retorno
    description: charge.description || '',
    discountPolicy: charge?.discounts.length > 0,
    interestPolicy: charge.interestPolicy !== 'no_interest',
    interestNegativation: charge?.negativation?.days_to_report !== null,
    dueDate: dueDate,
    payments: charge.paymentType.split(';').filter(Boolean),
    credit_card_uuid: { label: '', value: '' },
    interestFee: charge.interestFee || '1.0',
    interestFine: charge.interestFine || '2.0',
    daysToReport:
      charge?.negativation?.days_to_report?.toString() ||
      getDefaultDaysToReport(),
    interestInvoice: charge?.nfsesScheduled ? true : false,
    ...(!!charge?.issue_days && {
      hasIssueDays: !!charge?.issue_days,
      issueDays: charge?.issue_days || 0,
      issueBillingType: charge?.issue_billing_type || 'days_before_due_date'
    }),
    tags: convertTagsStringToArray(charge.tags, []),
    ...(charge?.nfsesScheduled && {
      iss_retention: charge.nfsesScheduled.iss_retention ? 'yes' : 'no',
      service_list_code: charge.nfsesScheduled.nfse_issuer_service?.uuid,
      amountCustom: charge.nfsesScheduled.amount
        ? charge.nfsesScheduled.amount.toString()
        : undefined,
      descriptionInvoice: charge.nfsesScheduled.description,
      nfse_issuer_uuid: charge.nfsesScheduled.nfse_issuer?.uuid,
      issue_when: isCustomPolicy ? 'on_custom_issued' : charge?.nfsePolicy,
      amount_type: charge.nfsesScheduled.amount_type,

      ...(isCustomPolicy && {
        due_date_period: charge.nfsePolicy as
          | 'on_before_due_date'
          | 'on_after_due_date',
        days: charge.nfsesScheduled.days as string
      }),
      nature_operation:
        enumNature[
          charge.nfsesScheduled.nature_operation as keyof typeof enumNature
        ],
      copy_description: charge.nfsesScheduled.copy_description,
      nfse_installment_scope:
        charge.nfsesScheduled.nfse_installment_scope || 'per_cycle'
    }),
    ...(parceledCreditCard && {
      max_installments: parceledCreditCard?.paymentable.max_installments ?? 12,
      max_installments_without_fee:
        parceledCreditCard?.paymentable.max_installments_without_fee ?? 12
    }),
    items: (() => {
      type FormItem = {
        uuid: string
        description?: string
        price: number
        product?: {
          label: string
          value: string
          total_cycles?: number
        }
        quantity: number
        total: number
        typedValue: ''
        enable_customer_select_quantity?: boolean
        tags?: { id: string; name: string; color?: string }[]
        bonus_policy?: 'no_bonus' | 'fixed' | 'percentage'
        bonus_amount?: number
      }

      const mappedItems: FormItem[] =
        charge.items && Array.isArray(charge.items)
          ? charge.items.map((item) => ({
              uuid: item.uuid,
              description: item.description,
              price: item.amount_unit,
              product: item.product
                ? {
                    label: item.product?.name,
                    value: item.product?.uuid,
                    // Cadência do plano: a trava de compatibilidade avalia os itens
                    // já existentes ao liberar (ou não) um produto novo na composição.
                    total_cycles: (item.product as any)?.total_cycles,
                    frequency: (item.product as any)?.frequency,
                    frequency_type: (item.product as any)?.frequency_type,
                    frequency_number: (item.product as any)?.frequency_number
                  }
                : undefined,
              quantity: item.quantity,
              total: item.amount_billed,
              typedValue: '' as const,
              enable_customer_select_quantity:
                (item as any).enable_customer_select_quantity || false,
              tags: ((item as any).tags || []).map((tag: any) => ({
                id: tag.uuid || tag.id,
                name: tag.name,
                color: tag.color
                  ? tag.color.startsWith('#')
                    ? tag.color
                    : `#${tag.color}`
                  : undefined
              })),
              bonus_policy: (item as any).bonus_policy || 'no_bonus',
              bonus_amount: Number((item as any).bonus_amount) || 0
            }))
          : []

      // For expired billings, add a "Multa/Juros" item with the difference
      const isExpiredBilling =
        charge.status === 'expiring' || charge.status === 'no_payment'
      const amountDifference =
        charge.amountUpdated - Number(charge.amountBilled)

      if (isExpiredBilling && amountDifference > 0) {
        mappedItems.push({
          uuid: uuidv4(),
          description: 'Multa/Juros',
          price: amountDifference,
          product: undefined,
          quantity: 1,
          total: amountDifference,
          typedValue: '' as const,
          enable_customer_select_quantity: false
        })
      }

      return mappedItems
    })()
  }
}

/**
 * Maps a charge to form values for duplication. Reuses edit mapper but strips
 * identity/payment state so the duplicate is a fresh pending billing.
 */
export const mapChargeToDuplicateFormValues = (
  charge: InvoiceParams
): Partial<ChargeRecurrenceBodyModalForm> => {
  const base = mapChargeToFormValues(charge)

  const items = (
    charge.items && Array.isArray(charge.items) ? charge.items : []
  ).map((item) => ({
    uuid: uuidv4(),
    description: item.description,
    price: item.amount_unit,
    product: item.product
      ? {
          label: item.product?.name,
          value: item.product?.uuid,
          total_cycles: (item.product as any)?.total_cycles,
          frequency: (item.product as any)?.frequency,
          frequency_type: (item.product as any)?.frequency_type,
          frequency_number: (item.product as any)?.frequency_number
        }
      : undefined,
    quantity: item.quantity,
    total: item.amount_billed,
    typedValue: '' as const,
    enable_customer_select_quantity:
      (item as any).enable_customer_select_quantity || false
  }))

  return {
    ...base,
    items,
    customerUuid: charge.customer?.uuid || charge.customerUuid || '',
    customer: {
      label: charge.customer?.name || '',
      value: charge.customer?.uuid || charge.customerUuid || ''
    },
    ...(charge.paymentTypeDetails && {
      payment_type_details: charge.paymentTypeDetails
    })
  }
}

/**
 * Maps a recurrence object to form values for editing
 */
export const mapRecurrenceToFormValues = (
  recurrence: RecurrencesParams
): Partial<ChargeRecurrenceBodyModalForm> => {
  // Handle discounts - agora retorna como parte dos valores do formulário
  const discounts: DiscountField[] = []
  if (
    recurrence?.discounts &&
    Array.isArray(recurrence.discounts) &&
    recurrence.discounts.length > 0
  ) {
    recurrence.discounts.forEach(({ amount, days, policy }) => {
      if (amount !== undefined && days !== undefined && policy !== undefined) {
        let amountToSave
        if (policy == 'percentage') {
          amountToSave = amount
        } else {
          amountToSave = formatCents(amount) || 0
        }

        // For recurrence, we store days as a separate property
        const dueDate = new Date()
        dueDate.setDate(dueDate.getDate() - Number(days))

        discounts.push({
          uuid: uuidv4(),
          amount: amountToSave.toString(),
          date: dueDate, // Required by DiscountField interface
          // Add days as a property for recurrence
          days: days.toString(),
          type: policy == 'percentage' ? '0' : '1'
        })
      }
    })
  }

  // Extract discount cycle config from the first discount (global setting shared across all discounts)
  const firstDiscount = recurrence?.discounts?.[0]
  const discountTotalCyclesRaw = firstDiscount?.totalCycles ?? 0
  const discountTotalCyclesValue =
    discountTotalCyclesRaw > 12 ? 13 : discountTotalCyclesRaw
  const discountTotalCyclesCustomValue =
    discountTotalCyclesRaw > 12 ? discountTotalCyclesRaw.toString() : ''
  const discountStartCycleValue = firstDiscount?.startCycle ?? 1

  // Handle metadata - será processado no retorno da função
  const internalIdentifiers: InternalIdentifier[] = []
  if (recurrence?.metadata && typeof recurrence.metadata === 'object') {
    Object.keys(recurrence.metadata).forEach((key) => {
      if (key && recurrence.metadata[key] !== undefined) {
        internalIdentifiers.push({
          name: key,
          value: recurrence.metadata[key]
        })
      }
    })
  }

  // O par canônico acompanha sempre a frequência: derivado nos presets, vindo do
  // registro na personalizada.
  const [frequencyType, frequencyNumber] = presetToPair(
    recurrence.frequency,
    recurrence.frequency_type,
    recurrence.frequency_number
  )

  // Map recurrence data to form values
  // Handle totalCycles > 12 by setting totalCycles to 13 (Outros) and totalCyclesCustom to the actual value
  const totalCyclesValue =
    recurrence.totalCycles > 12 ? 13 : recurrence.totalCycles
  const totalCyclesCustomValue =
    recurrence.totalCycles > 12 ? recurrence.totalCycles.toString() : undefined

  const isCustomPolicy =
    recurrence?.nfsePolicy === 'on_before_due_date' ||
    recurrence?.nfsePolicy === 'on_after_due_date'

  const bonusSettings = recurrence.bonusSettings
    ? {
        cyclesCount:
          recurrence.bonusSettings.cyclesCount > 12
            ? 13
            : recurrence.bonusSettings.cyclesCount,
        cyclesCountCustom:
          recurrence.bonusSettings.cyclesCount > 12
            ? recurrence.bonusSettings.cyclesCount.toString()
            : '',
        type: recurrence.bonusSettings.type,
        // Format amount based on type, similar to EditRecurrenceBodyModal
        amount:
          recurrence.bonusSettings.type === 'value'
            ? formatCents(recurrence.bonusSettings.amount || '')
            : recurrence.bonusSettings.amount || '',
        startCycle: recurrence.bonusSettings.startCycle || 1
      }
    : {
        cyclesCount: 1,
        cyclesCountCustom: '',
        type: 'value' as const,
        amount: '',
        startCycle: 1
      }

  return {
    description: recurrence.description || '',
    hasIdentifyInternal: internalIdentifiers.length > 0,
    internalIdentifiers,
    discounts, // Adicionar descontos ao retorno
    discountTotalCycles: discountTotalCyclesValue,
    discountTotalCyclesCustom: discountTotalCyclesCustomValue,
    discountStartCycle: discountStartCycleValue,
    hasBonus: recurrence.hasBonus || false,
    bonusSettings,
    discountPolicy: recurrence.discountPolicy,
    interestPolicy: recurrence.interestPolicy,
    interestFee: recurrence.interestFee || '1.0',
    interestFine: recurrence.interestFine || '2.0',
    dueDay: recurrence.dueDay?.toString(),
    // Sem limite próprio de pagamento, usa o padrão do merchant (igual à criação).
    expirationField: recurrence.expirationField?.toString() ?? getLimitDays(),
    frequency: FREQUENCY_NAME_TO_ENUM[recurrence.frequency] ?? 1,
    frequency_type: frequencyType,
    frequency_number: frequencyNumber,
    totalCycles: totalCyclesValue,
    totalCyclesCustom: totalCyclesCustomValue,
    payments: recurrence.paymentType.split(';').filter(Boolean),
    interestNegativation: recurrence?.negativation?.days_to_report !== null,
    daysToReport:
      recurrence?.negativation?.days_to_report?.toString() ||
      getDefaultDaysToReport(),
    interestInvoice: recurrence?.nfsesScheduled ? true : false,
    ...(recurrence?.nfsesScheduled && {
      interestInvoice: true,
      iss_retention: recurrence.nfsesScheduled.iss_retention ? 'yes' : 'no',
      service_list_code:
        recurrence.nfsesScheduled.nfse_issuer_service?.uuid || '',
      amountCustom: recurrence.nfsesScheduled.amount
        ? recurrence.nfsesScheduled.amount.toString()
        : undefined,
      descriptionInvoice: recurrence.nfsesScheduled.description || '',
      nfse_issuer_uuid: recurrence.nfsesScheduled.nfse_issuer?.uuid || '',
      issue_when: isCustomPolicy
        ? 'on_custom_issued'
        : recurrence?.nfsePolicy || 'on_billing_issued',

      ...(isCustomPolicy && {
        due_date_period: recurrence.nfsePolicy as
          | 'on_before_due_date'
          | 'on_after_due_date',
        days: recurrence.nfsesScheduled.days as string
      }),

      amount_type: recurrence.nfsesScheduled.amount_type || 'amount_paid',
      nature_operation:
        (recurrence.nfsesScheduled.nature_operation &&
          enumNature[
            recurrence.nfsesScheduled
              .nature_operation as keyof typeof enumNature
          ]) ||
        '1',
      copy_description: !!recurrence.nfsesScheduled.copy_description,
      nfse_installment_scope:
        recurrence.nfsesScheduled.nfse_installment_scope || 'per_cycle'
    }),
    ...(recurrence?.issue_days && {
      hasIssueDays: !!recurrence?.issue_days,
      issueDays: recurrence?.issue_days || 0,
      issueBillingType: recurrence?.issue_billing_type || 'days_before_due_date'
    }),
    tags: convertTagsStringToArray(recurrence.tags, []),
    // Map items directly from recurrence.items
    items:
      recurrence.items && Array.isArray(recurrence.items)
        ? recurrence.items.map((item) => ({
            uuid: item.uuid,
            description: item.description || undefined,
            price: item.amount_unit,
            product: item.product
              ? {
                  label: item.product?.name,
                  value: item.product?.uuid,
                  total_cycles: (item.product as any).total_cycles,
                  frequency: (item.product as any)?.frequency,
                  frequency_type: (item.product as any)?.frequency_type,
                  frequency_number: (item.product as any)?.frequency_number
                }
              : undefined,
            quantity: item.quantity,
            total: item.amount_billed,
            typedValue: '',
            enable_customer_select_quantity:
              (item as any).enable_customer_select_quantity || false
          }))
        : []
  }
}

/**
 * Maps a recurrence to form values for duplication. Reuses the edit mapper but
 * strips runtime/identity fields so the duplicate is a fresh standalone recurrence.
 */
export const mapRecurrenceToDuplicateFormValues = (
  recurrence: RecurrencesParams
): Partial<ChargeRecurrenceBodyModalForm> => {
  const base = mapRecurrenceToFormValues(recurrence)

  const items = (base.items || []).map((item) => ({
    ...item,
    uuid: uuidv4()
  }))

  const discounts = (base.discounts || []).map((discount) => ({
    ...discount,
    uuid: uuidv4()
  }))

  return {
    ...base,
    items,
    discounts,
    customerUuid: recurrence.customer?.uuid || '',
    customer: {
      label: recurrence.customer?.name || '',
      value: recurrence.customer?.uuid || ''
    },
    ...(recurrence.paymentTypeDetails && {
      payment_type_details: recurrence.paymentTypeDetails
    })
  } as Partial<ChargeRecurrenceBodyModalForm>
}

export const SENSITIVE_FIELD_LABELS: Record<string, string> = {
  due_date: 'Vencimento',
  amount_billed: 'Valor',
  payment_type: 'Forma de pagamento',
  discounts: 'Desconto',
  interest_policy: 'Juros e multa'
}

const CREDIT_CARD_VARIANTS = [
  'credit_card_recurring',
  'credit_card_installments'
]

// Espelha a normalização feita no form (BodyModalNewChargeOrRecurrence): quando
// existe qualquer variante de cartão, o form também marca 'credit_card' para
// refletir o card visual. O baseline precisa aplicar a mesma regra, senão um
// pagamento inalterado por cartão é sinalizado como "Forma de pagamento" alterada.
const normalizePayments = (payments: unknown): string[] => {
  const list = Array.isArray(payments) ? (payments as string[]) : []
  if (
    !list.includes('credit_card') &&
    list.some((p) => CREDIT_CARD_VARIANTS.includes(p))
  ) {
    return [...list, 'credit_card']
  }
  return list
}

const sortedPaymentsKey = (payments: unknown): string =>
  normalizePayments(payments).slice().sort().join(';')

const stableJson = (value: unknown): string => {
  try {
    return JSON.stringify(value ?? null)
  } catch {
    return ''
  }
}

const sameDay = (a: unknown, b: unknown): boolean => {
  if (!a || !b) return a === b
  const da = moment(a as string | Date)
  const db = moment(b as string | Date)
  if (!da.isValid() || !db.isValid()) return false
  return da.format('YYYY-MM-DD') === db.format('YYYY-MM-DD')
}

export const getChangedSensitiveFields = (
  currentFormValues: Partial<ChargeRecurrenceBodyModalForm>,
  editData: InvoiceParams
): string[] => {
  if (!editData) return []
  const original = mapChargeToFormValues(editData)
  const changed: string[] = []

  if (!sameDay(original.dueDate, currentFormValues.dueDate)) {
    changed.push('due_date')
  }

  // O valor da cobrança mora nos `items` (calculateTotalValue), e o campo
  // amountBilled não é sincronizado quando os itens mudam — então comparamos
  // também o total calculado dos itens pra captar a mudança de valor.
  if (
    (original.amountBilled ?? '').toString() !==
      (currentFormValues.amountBilled ?? '').toString() ||
    calculateTotalValue((currentFormValues.items as unknown[]) ?? []) !==
      calculateTotalValue((original.items as unknown[]) ?? [])
  ) {
    changed.push('amount_billed')
  }

  if (
    sortedPaymentsKey(original.payments) !==
    sortedPaymentsKey(currentFormValues.payments)
  ) {
    changed.push('payment_type')
  }

  const originalDiscount = {
    enabled: !!original.discountPolicy,
    discounts: original.discounts ?? []
  }
  const currentDiscount = {
    enabled: !!currentFormValues.discountPolicy,
    discounts: currentFormValues.discounts ?? []
  }
  if (stableJson(originalDiscount) !== stableJson(currentDiscount)) {
    changed.push('discounts')
  }

  if (!!original.interestPolicy !== !!currentFormValues.interestPolicy) {
    changed.push('interest_policy')
  }

  // Bônus é um conceito de recorrência; cobranças não possuem bônus, então não
  // é comparado aqui (este fluxo só roda em edição de cobrança emitida).

  return changed
}

// Campos de config da nota cuja alteração defasa diretamente a nota emitida.
const NFSE_CONFIG_FIELDS = [
  'interestInvoice',
  'descriptionInvoice',
  'amount_type',
  'nature_operation',
  'iss_retention',
  'nfse_issuer_uuid',
  'service_list_code',
  'copy_description',
  'nfse_installment_scope'
] as const

export type NfseDialogMode = 'none' | 'reissue' | 'emit' | 'blocked'

export interface NfseEditContext {
  // Tem alguma nota (qualquer status) ligada à cobrança.
  hasNote: boolean
  // Tem nota AGENDADA (nfse_scheduled) — a policy vai emitir sozinha quando a
  // cobrança abrir; não faz sentido perguntar "emitir agora".
  hasScheduled: boolean
  // Alguma nota em 'issued' (em processamento na prefeitura) → bloqueia ação.
  processing: boolean
  // Nota confirmada cancelável (a defasada que pode ser cancelada+reemitida).
  confirmedNfse?: { uuid: string; nfse_number: string | null }
  // O form tem emissão de nota ativa (toggle "emitir nota fiscal").
  formHasNfse: boolean
  // Defasagem detectada localmente: config da nota OU valor/descrição que a alimenta.
  nfseChanged: boolean
}

// Detecção de defasagem da nota feita NO FRONT (diff local form x editData).
// O backend ainda valida a intenção/permissões; aqui só decidimos o que mostrar.
export const getNfseEditContext = (
  currentFormValues: Partial<ChargeRecurrenceBodyModalForm>,
  editData: InvoiceParams
): NfseEditContext => {
  const nfses = editData?.nfses ?? []
  const processing = nfses.some((n) => n?.status === 'issued')

  const confirmed =
    editData?.cancelableNfses?.[0] ??
    nfses.find((n) => n?.status === 'confirmed')
  const confirmedNfse = confirmed
    ? { uuid: confirmed.uuid, nfse_number: confirmed.nfse_number ?? null }
    : undefined

  const original = editData
    ? (mapChargeToFormValues(
        editData
      ) as Partial<ChargeRecurrenceBodyModalForm>)
    : {}
  const formHasNfse = !!currentFormValues.interestInvoice

  const norm = (v: unknown): string =>
    v === undefined || v === null ? '' : String(v)

  const configChanged = NFSE_CONFIG_FIELDS.some(
    (f) =>
      norm((original as Record<string, unknown>)[f]) !==
      norm((currentFormValues as Record<string, unknown>)[f])
  )

  // Defasagem indireta: o valor da cobrança alimenta a nota quando o tipo é
  // baseado em valor (original/pago); 'amount_custom' não depende do valor.
  // O valor mora nos `items` (calculateTotalValue), não num campo amountBilled —
  // então comparamos o total calculado dos itens (atual x original).
  const amountChanged =
    norm(original.amountBilled) !== norm(currentFormValues.amountBilled) ||
    calculateTotalValue((currentFormValues.items as unknown[]) ?? []) !==
      calculateTotalValue((original.items as unknown[]) ?? [])
  const valueDrivenType =
    currentFormValues.amount_type === 'amount_issued' ||
    currentFormValues.amount_type === 'amount_paid'

  // Descrição da cobrança alimenta a nota quando copy_description está ligado.
  const billingDescChanged =
    norm(original.description) !== norm(currentFormValues.description)
  const copyDesc = !!currentFormValues.copy_description

  const nfseChanged =
    formHasNfse &&
    (configChanged ||
      (amountChanged && valueDrivenType) ||
      (billingDescChanged && copyDesc))

  return {
    hasNote: nfses.length > 0,
    hasScheduled: !!editData?.nfsesScheduled,
    processing,
    confirmedNfse,
    formHasNfse,
    nfseChanged
  }
}

// Deriva o modo do dialog de nota a partir do contexto de edição.
export const resolveNfseDialogMode = (ctx: NfseEditContext): NfseDialogMode => {
  if (!ctx.formHasNfse) return 'none'
  if (ctx.processing && ctx.nfseChanged) return 'blocked'
  if (ctx.confirmedNfse && ctx.nfseChanged) return 'reissue'
  // Nota AGENDADA já vai ser emitida pela policy quando a cobrança abrir — não
  // perguntamos "emitir agora" (o dialog dizia, erradamente, "ainda não tem nota
  // fiscal"). O passo de emissão só vale quando NÃO há nota agendada.
  if (
    !ctx.confirmedNfse &&
    !ctx.processing &&
    !ctx.hasScheduled &&
    (ctx.nfseChanged || !ctx.hasNote)
  ) {
    return 'emit'
  }
  return 'none'
}

export type NfseEmitChoice = 'now' | 'policy'

export interface NfseActionInputs {
  // Só cobrança JÁ emitida ao cliente carrega ação de nota.
  isIssuedChargeEdit: boolean
  mode: NfseDialogMode
  permissions: { canCancel: boolean; canEmit: boolean }
  // Nota confirmada alvo (defasada) — necessária para reissue/cancel.
  confirmedNfseUuid?: string | null
  // Toggle "reemitir a nota atualizada".
  reemit: boolean
  // Toggle "cancelar a nota atual" (sem emitir outra).
  cancelOld: boolean
  justificativa: string
  // 'now' = emitir já no save; 'policy' = deixar a policy emitir depois.
  emitChoice: NfseEmitChoice
}

// Monta os params de AÇÃO de nota fiscal enviados no save da cobrança
// (PATCH /v3/billings). A emissão/reemissão é DIRETA no backend
// (`process_nfse_action!` → `Nfse::CancelAndReissue`): emite a nota nova com a
// config recém-salva em nfse_scheduled_attributes e, na reemissão, cancela a
// antiga de forma assíncrona após o sucesso. Sem modal de nota pré-preenchido.
//
// Retorna {} quando não há ação (sem permissão, sem nota alvo, ou "deixar a
// policy emitir depois"). A config da nota vai sempre em nfse_scheduled_attributes
// à parte; aqui só decidimos a ação imperativa.
export const buildNfseActionPayload = (
  inputs: NfseActionInputs
): Record<string, unknown> => {
  const {
    isIssuedChargeEdit,
    mode,
    permissions,
    confirmedNfseUuid,
    reemit,
    cancelOld,
    justificativa,
    emitChoice
  } = inputs

  if (!isIssuedChargeEdit) return {}

  if (mode === 'reissue') {
    if (!permissions.canCancel || !confirmedNfseUuid) return {}

    // Reemitir: emite a nota nova (config atualizada) e cancela a antiga.
    if (reemit) {
      return {
        nfse_action: 'reissue',
        nfse_cancel_uuid: confirmedNfseUuid,
        nfse_cancel_justificativa: justificativa
      }
    }
    // Só "cancelar sozinho": anula a nota atual, sem emitir outra.
    if (cancelOld) {
      return {
        nfse_action: 'cancel',
        nfse_cancel_uuid: confirmedNfseUuid,
        nfse_cancel_justificativa: justificativa
      }
    }
    return {}
  }

  // Emitir (cobrança sem nota): "emitir agora" dispara a emissão direta no
  // backend a partir do nfse_scheduled salvo. "Deixar a policy emitir depois"
  // não envia ação.
  if (mode === 'emit') {
    if (permissions.canEmit && emitChoice === 'now') {
      return { nfse_action: 'emit', nfse_emit_now: true }
    }
    return {}
  }

  return {}
}
